"use client";

import { Check, X } from "lucide-react";
import { ScrollReveal } from "../scroll-reveal";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { useState } from "react";

type Props = {
  pricingBadge?: boolean;
  pricingTitle?: string;
  showCustomCTA?: boolean;
};

export default function Pricing({
  pricingBadge = true,
  pricingTitle = "Chọn gói phù hợp với nhu cầu của bạn",
  showCustomCTA = true,
}: Props) {
  const [annualBilling, setAnnualBilling] = useState(false);

  return (
    <section id="pricing" className={`w-full ${pricingBadge ? 'py-20' : 'py-0'} bg-white overflow-hidden`}>
      <div className="container px-4 md:px-6">
        <ScrollReveal>
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
            {pricingBadge && (
              <div className="inline-block rounded-full bg-indigo-100 px-4 py-1.5  font-medium text-indigo-600 mb-2">
                Bảng giá linh hoạt
              </div>
            )}

            <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
              {pricingTitle}
            </h2>
            <p className="max-w-[800px] text-muted-foreground md:text-lg">
              Chúng tôi cung cấp nhiều gói dịch vụ khác nhau để phù hợp với quy
              mô và ngân sách của doanh nghiệp
            </p>
          </div>
        </ScrollReveal>

        <div className="flex items-center justify-center space-x-4 mb-12">
          <span
            className={` font-medium whitespace-nowrap ${
              !annualBilling ? "text-indigo-600" : "text-gray-500"
            }`}
          >
            Thanh toán tháng
          </span>
          <div className="flex items-center space-x-2">
            <Switch
              id="billing-toggle"
              checked={annualBilling}
              onCheckedChange={setAnnualBilling}
              className="data-[state=checked]:bg-indigo-500"
            />
            <Label htmlFor="billing-toggle" className="sr-only">
              Toggle annual billing
            </Label>
          </div>
          <span
            className={` font-medium ${
              annualBilling ? "text-indigo-600" : "text-gray-500"
            }`}
          >
            Thanh toán năm{" "}
            <span className="inline-block ml-1 px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full  font-semibold">
              Tiết kiệm 20%
            </span>
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <ScrollReveal direction="up" delay={0.1} className="h-full">
            <div className="h-full bg-white rounded-2xl border border-indigo-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 p-8 flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2">Cơ bản</h3>

                <div className="flex items-baseline mb-2">
                  <span className="text-4xl font-bold">
                    {annualBilling ? "799k" : "999k"}
                  </span>
                  <span className="text-muted-foreground ml-1">/tháng</span>
                </div>
                {annualBilling && (
                  <p className=" text-indigo-600 font-medium">
                    Thanh toán 9.588.000đ/năm
                  </p>
                )}
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Không giới hạn đặt lịch</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quản lý khách hàng cơ bản</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Thanh toán & hóa đơn</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Báo cáo doanh thu cơ bản</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Hỗ trợ email</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-400">Marketing tự động</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-400">Quản lý nhiều chi nhánh</span>
                </li>
              </ul>
              <Button className="w-full bg-white hover:bg-indigo-50 text-indigo-600 border border-indigo-200 hover:border-indigo-300">
                Dùng thử miễn phí
              </Button>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" className="h-full">
            <div className="h-full relative bg-gradient-to-b from-indigo-500 to-purple-500 text-white rounded-2xl shadow-xl p-8 flex flex-col transform hover:-translate-y-1 transition-all duration-300">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-amber-500 px-4 py-1 rounded-full  font-bold shadow-lg">
                PHỔ BIẾN NHẤT
              </div>
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2">Chuyên nghiệp</h3>

                <div className="flex items-baseline mb-2">
                  <span className="text-4xl font-bold">
                    {annualBilling ? "1.599k" : "1.999k"}
                  </span>
                  <span className="text-white/80 ml-1">/tháng</span>
                </div>
                {annualBilling && (
                  <p className=" text-white/90 font-medium">
                    Thanh toán 19.188.000đ/năm
                  </p>
                )}
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                  <span>Tất cả tính năng của gói Cơ bản</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quản lý khách hàng nâng cao</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                  <span>Marketing tự động</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quản lý kho hàng</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                  <span>Hỗ trợ 24/7</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-white mr-2 mt-0.5 flex-shrink-0" />
                  <span>Báo cáo chi tiết</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-white/50 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-white/70">Quản lý nhiều chi nhánh</span>
                </li>
              </ul>
              <Button className="w-full bg-white text-indigo-600 hover:bg-indigo-50 transition-colors">
                Dùng thử miễn phí
              </Button>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.2} className="h-full">
            <div className="h-full bg-white rounded-2xl border border-indigo-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 p-8 flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2">Doanh nghiệp</h3>

                <div className="flex items-baseline mb-2">
                  <span className="text-4xl font-bold">
                    {annualBilling ? "3.199k" : "3.999k"}
                  </span>
                  <span className="text-muted-foreground ml-1">/tháng</span>
                </div>
                {annualBilling && (
                  <p className=" text-indigo-600 font-medium">
                    Thanh toán 38.388.000đ/năm
                  </p>
                )}
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Tất cả tính năng của gói Chuyên nghiệp</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quản lý nhiều chi nhánh</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>API tích hợp</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Phân tích kinh doanh nâng cao</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quản lý nhân sự chuyên sâu</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Hỗ trợ 24/7 ưu tiên</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Tùy chỉnh theo yêu cầu</span>
                </li>
              </ul>
              <Button className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                Dùng thử miễn phí
              </Button>
            </div>
          </ScrollReveal>
        </div>

        {showCustomCTA && (
          <ScrollReveal direction="up" delay={0.3} className="mt-16">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-3xl p-8 md:p-10 max-w-5xl mx-auto">
              <div className="text-center max-w-3xl mx-auto">
                <h3 className="text-2xl font-bold mb-4">
                  Bạn cần giải pháp tùy chỉnh?
                </h3>
                <p className="text-muted-foreground mb-6">
                  Chúng tôi hiểu rằng mỗi spa có nhu cầu riêng biệt. Hãy liên hệ
                  với đội ngũ tư vấn của chúng tôi để nhận được giải pháp phù
                  hợp nhất.
                </p>
                <Button className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0 shadow-lg shadow-indigo-500/25">
                  Liên hệ tư vấn
                </Button>
              </div>
            </div>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}
